import { api } from "@config/axios/axios.config";
import { SERVER_ROUTES } from "@constants/api.routes";

export type CourseContentSubjectRef = {
	uuid: string;
	name: string;
	description?: string | null;
};

const UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isLikelyUuid(value: string): boolean {
	return UUID_RE.test(value.trim());
}

/** True when the display name is missing or is just the subject UUID. */
export function subjectNeedsNameResolution(subject: CourseContentSubjectRef): boolean {
	const name = subject.name?.trim() ?? "";
	if (!name) return true;
	if (name === subject.uuid) return true;
	return isLikelyUuid(name);
}

function looksLikeSubjectRecord(record: Record<string, unknown>): boolean {
	// Skip course-content / course wrappers that also have a uuid.
	if ("contentCode" in record || "courseUuid" in record || "courseId" in record) return false;
	if (Array.isArray(record.subjects) || Array.isArray(record.subjectIds)) return false;
	if (Array.isArray(record.courseContentSubjects)) return false;
	return (
		typeof record.uuid === "string" ||
		typeof record.subjectUuid === "string" ||
		typeof record.subjectId === "string" ||
		(record.subject != null && typeof record.subject === "object")
	);
}

function normalizeSubjectRef(item: unknown): CourseContentSubjectRef | null {
	if (!item || typeof item !== "object") return null;
	const record = item as Record<string, unknown>;
	if (!looksLikeSubjectRecord(record)) return null;

	const nestedSubject =
		record.subject && typeof record.subject === "object"
			? (record.subject as Record<string, unknown>)
			: null;

	// Prefer nested subject identity when API returns courseContentSubjects links.
	const uuidCandidate =
		(typeof nestedSubject?.uuid === "string" && nestedSubject.uuid) ||
		(typeof record.subjectUuid === "string" && record.subjectUuid) ||
		(typeof record.uuid === "string" && record.uuid) ||
		"";

	const uuid = uuidCandidate.trim();
	if (!uuid) return null;

	const nameCandidate =
		(typeof nestedSubject?.name === "string" && nestedSubject.name.trim()) ||
		(typeof record.name === "string" && record.name.trim()) ||
		"";

	const descriptionCandidate =
		(typeof nestedSubject?.description === "string" && nestedSubject.description) ||
		(typeof record.description === "string" && record.description) ||
		null;

	// Prefer a real label; leave uuid only as a temporary placeholder for resolution.
	return {
		uuid,
		name: nameCandidate || uuid,
		...(descriptionCandidate?.trim() ? { description: descriptionCandidate } : {}),
	};
}

function dedupeSubjects(subjects: CourseContentSubjectRef[]): CourseContentSubjectRef[] {
	const seen = new Set<string>();
	const result: CourseContentSubjectRef[] = [];
	for (const subject of subjects) {
		if (seen.has(subject.uuid)) continue;
		seen.add(subject.uuid);
		result.push(subject);
	}
	return result;
}

/**
 * Prefer entries that already have a human-readable name when merging sources.
 */
function mergeTwoSubjectRefs(
	existing: CourseContentSubjectRef,
	incoming: CourseContentSubjectRef,
	options?: { preferIncomingDescription?: boolean },
): CourseContentSubjectRef {
	let merged: CourseContentSubjectRef = existing;

	if (subjectNeedsNameResolution(existing) && !subjectNeedsNameResolution(incoming)) {
		merged = {
			...incoming,
			description: incoming.description ?? existing.description,
		};
	} else if (!existing.description?.trim() && incoming.description?.trim()) {
		merged = { ...existing, description: incoming.description };
	}

	if (options?.preferIncomingDescription && incoming.description?.trim()) {
		merged = { ...merged, description: incoming.description.trim() };
	}

	return merged;
}

export function mergeSubjectRefs(
	...groups: CourseContentSubjectRef[][]
): CourseContentSubjectRef[] {
	const byUuid = new Map<string, CourseContentSubjectRef>();
	for (const group of groups) {
		for (const subject of group) {
			const existing = byUuid.get(subject.uuid);
			if (!existing) {
				byUuid.set(subject.uuid, subject);
				continue;
			}
			byUuid.set(subject.uuid, mergeTwoSubjectRefs(existing, subject));
		}
	}
	return [...byUuid.values()];
}

/** Later groups win for `description` when present (e.g. `contents[].subjects`). */
export function mergeSubjectRefsPreferLaterDescriptions(
	...groups: CourseContentSubjectRef[][]
): CourseContentSubjectRef[] {
	const byUuid = new Map<string, CourseContentSubjectRef>();
	for (const group of groups) {
		for (const subject of group) {
			const existing = byUuid.get(subject.uuid);
			if (!existing) {
				byUuid.set(subject.uuid, subject);
				continue;
			}
			byUuid.set(
				subject.uuid,
				mergeTwoSubjectRefs(existing, subject, { preferIncomingDescription: true }),
			);
		}
	}
	return [...byUuid.values()];
}

function collectSubjectsFromContentBlocksSubjectsArray(blocks: unknown[]): CourseContentSubjectRef[] {
	let collected: CourseContentSubjectRef[] = [];
	for (const block of blocks) {
		if (!block || typeof block !== "object") continue;
		const subjects = (block as Record<string, unknown>).subjects;
		if (!Array.isArray(subjects)) continue;
		const parsed = subjects
			.map(normalizeSubjectRef)
			.filter((item): item is CourseContentSubjectRef => Boolean(item));
		collected = mergeSubjectRefs(collected, parsed);
	}
	return collected;
}

/**
 * Pull subject refs from course details, content payloads, and nested content arrays.
 */
export function collectSubjectsFromCoursePayload(source: unknown): CourseContentSubjectRef[] {
	if (!source) return [];

	if (Array.isArray(source)) {
		if (source.every(item => typeof item === "string")) {
			return dedupeSubjects(
				source
					.filter((id): id is string => typeof id === "string" && id.trim().length > 0)
					.map(uuid => ({ uuid, name: uuid })),
			);
		}
		const fromItems = source.flatMap(item => {
			const asSubject = normalizeSubjectRef(item);
			if (asSubject) return [asSubject];
			return collectSubjectsFromCoursePayload(item);
		});
		return dedupeSubjects(fromItems);
	}

	if (typeof source !== "object") return [];
	const record = source as Record<string, unknown>;

	let collected: CourseContentSubjectRef[] = [];

	for (const key of ["subjects", "courseSubjects", "courseContentSubjects"] as const) {
		const value = record[key];
		if (Array.isArray(value) && value.length > 0) {
			const parsed = value
				.map(normalizeSubjectRef)
				.filter((item): item is CourseContentSubjectRef => Boolean(item));
			collected = mergeSubjectRefs(collected, parsed);
		}
	}

	if (Array.isArray(record.subjectIds) && record.subjectIds.length > 0) {
		const parsed = record.subjectIds
			.filter((id): id is string => typeof id === "string" && id.trim().length > 0)
			.map(uuid => ({ uuid, name: uuid }));
		collected = mergeSubjectRefs(collected, parsed);
	}

	for (const key of [
		"contents",
		"content",
		"courseContent",
		"data",
	] as const) {
		const nested = record[key];
		if (nested == null) continue;
		if (Array.isArray(nested)) {
			for (const item of nested) {
				collected = mergeSubjectRefs(collected, collectSubjectsFromCoursePayload(item));
			}
		} else {
			collected = mergeSubjectRefs(collected, collectSubjectsFromCoursePayload(nested));
		}
	}

	return collected;
}

/**
 * Scheme-of-studies subjects from GET /course/{uuid} `contents` only.
 * `description` prefers `contents[].subjects[].description`.
 */
export function collectSubjectsFromCourseDetailsContents(courseDetails: unknown): CourseContentSubjectRef[] {
	if (!courseDetails || typeof courseDetails !== "object") return [];
	const contentsBlocks = (courseDetails as Record<string, unknown>).contents;
	if (!Array.isArray(contentsBlocks)) return [];

	let fromBlocks: CourseContentSubjectRef[] = [];
	for (const block of contentsBlocks) {
		fromBlocks = mergeSubjectRefs(fromBlocks, collectSubjectsFromCoursePayload(block));
	}

	return mergeSubjectRefsPreferLaterDescriptions(
		fromBlocks,
		collectSubjectsFromContentBlocksSubjectsArray(contentsBlocks),
	);
}

type ApiEnvelope = {
	data?: unknown;
};

async function fetchSubjectDetailsByUuid(
	uuid: string,
): Promise<{ name: string | null; description: string | null }> {
	try {
		const response = await api.get<ApiEnvelope & { name?: string; description?: string | null }>(
			SERVER_ROUTES.INSTITUTE.GET_SUBJECT(uuid),
		);
		const envelope = response as ApiEnvelope & { name?: string; description?: string | null };
		let name: string | null =
			typeof envelope.name === "string" && envelope.name.trim() ? envelope.name.trim() : null;
		let description: string | null =
			typeof envelope.description === "string" && envelope.description.trim()
				? envelope.description.trim()
				: null;

		const data = envelope.data;
		if (data && typeof data === "object") {
			const record = data as Record<string, unknown>;
			if (!name && typeof record.name === "string" && record.name.trim()) {
				name = record.name.trim();
			}
			if (!description && typeof record.description === "string" && record.description.trim()) {
				description = record.description.trim();
			}
			const nested = record.data;
			if (nested && typeof nested === "object") {
				const nestedRecord = nested as Record<string, unknown>;
				if (!name && typeof nestedRecord.name === "string" && nestedRecord.name.trim()) {
					name = nestedRecord.name.trim();
				}
				if (
					!description &&
					typeof nestedRecord.description === "string" &&
					nestedRecord.description.trim()
				) {
					description = nestedRecord.description.trim();
				}
			}
		}
		return { name, description };
	} catch {
		// Ignore — leave placeholders.
	}
	return { name: null, description: null };
}

/** Resolve placeholder UUID names (and missing descriptions) via GET /subjects/{uuid}. */
export async function resolveSubjectDisplayNames(
	subjects: CourseContentSubjectRef[],
): Promise<CourseContentSubjectRef[]> {
	if (subjects.length === 0) return subjects;

	return Promise.all(
		subjects.map(async subject => {
			const needsName = subjectNeedsNameResolution(subject);
			const needsDescription = !subject.description?.trim();
			if (!needsName && !needsDescription) return subject;

			const details = await fetchSubjectDetailsByUuid(subject.uuid);
			const nextName =
				needsName && details.name && details.name !== subject.uuid ? details.name : subject.name;
			const nextDescription = subject.description?.trim() || details.description || undefined;
			return {
				uuid: subject.uuid,
				name: nextName,
				...(nextDescription ? { description: nextDescription } : {}),
			};
		}),
	);
}

export type FetchCourseContentSubjectsOptions = {
	/** When provided, subjects are merged from this payload and GET /course/{uuid} is skipped. */
	existingCourseDetails?: unknown;
	/** When false, never call GET /subjects/{uuid} — use course/contents payloads only. */
	resolveIndividualSubjects?: boolean;
	/** When true, only fetch GET /course/{uuid}/contents (no course-details refetch). */
	contentsApiOnly?: boolean;
};

/**
 * Loads already-added scheme-of-studies subjects for a course.
 * Prefers GET /course/{uuid}/contents, then course details, then resolves names.
 */
export async function fetchCourseContentSubjects(
	courseUuid: string,
	options?: FetchCourseContentSubjectsOptions,
): Promise<CourseContentSubjectRef[]> {
	const resolveIndividualSubjects = options?.resolveIndividualSubjects !== false;
	let collected = collectSubjectsFromCoursePayload(options?.existingCourseDetails);

	const attempts: Array<() => Promise<CourseContentSubjectRef[]>> = [
		async () => {
			const contentsResponse = await api.get<ApiEnvelope>(
				SERVER_ROUTES.INSTITUTE.GET_COURSE_CONTENTS(courseUuid),
			);
			return collectSubjectsFromCoursePayload(contentsResponse);
		},
	];

	if (!options?.contentsApiOnly && options?.existingCourseDetails == null) {
		attempts.push(async () => {
			const detailsResponse = await api.get<ApiEnvelope>(
				SERVER_ROUTES.INSTITUTE.GET_COURSE_DETAILS(courseUuid),
			);
			return collectSubjectsFromCoursePayload(detailsResponse);
		});
	}
	for (const attempt of attempts) {
		try {
			const subjects = await attempt();
			collected = mergeSubjectRefs(collected, subjects);
		} catch {
			// Try the next source when an endpoint is missing or fails.
		}
	}

	if (!resolveIndividualSubjects) {
		return collected.filter(subject => !subjectNeedsNameResolution(subject));
	}

	return resolveSubjectDisplayNames(collected);
}
